import React, { useState } from "react";
import { Check, ArrowRight, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import api from "@/lib/api";
import { IMG, BUDGETS } from "@/data/site";

const TYPES = ["Wall Tiling", "Floor Tiling", "Full Bathroom Installation", "Wet Room", "Kitchen Splashback", "Feature Wall", "Handyman"];

export default function Quote() {
  const [step, setStep] = useState(1);
  const [data, setData] = useState({ type: "Full Bathroom Installation", size: "", condition: "", materials: "", timeframe: "", budget: "Standard", name: "", email: "", phone: "", postcode: "", message: "" });
  const [done, setDone] = useState(false);
  const set = (k, v) => setData({ ...data, [k]: v });

  const submit = async () => {
    if (!data.name || !data.email) { toast.error("Please add your name and email."); return; }
    try {
      await api.post("/enquiries", {
        kind: "quote", name: data.name, email: data.email, phone: data.phone, postcode: data.postcode,
        message: data.message,
        payload: { project_type: data.type, room_size: data.size, current_condition: data.condition, materials_needed: data.materials, timeframe: data.timeframe, budget: data.budget },
      });
      setDone(true);
      toast.success("Quote request sent!");
    } catch (e) { toast.error("Could not send. Please try again."); }
  };

  if (done) return (
    <div data-testid="quote-page">
      <PageHero eyebrow="Thank you" title="Your quote request is in" subtitle="We'll review the details and get back to you with a clear, itemised estimate." image={IMG.bath[3]} />
      <div className="container-rossi py-20 text-center"><Check size={44} className="text-rossi-gold mx-auto" /></div>
    </div>
  );

  return (
    <div data-testid="quote-page">
      <Seo title="Get a Free Quote" description="Request a free, no-obligation tiling or bathroom installation quote from Rossi Ceramics in Stockport and the North West." />
      <PageHero eyebrow="Free & no-obligation" title="Get a quote" subtitle="A few quick questions and Vincent will prepare a tailored estimate." image={IMG.bath[3]} />

      <section className="container-rossi py-16 max-w-2xl">
        <div className="card-lux p-7 sm:p-10">
          <div className="flex gap-2 mb-8">
            {[1, 2, 3].map((n) => <div key={n} className={`h-1.5 flex-1 rounded-full ${step >= n ? "bg-rossi-gold" : "bg-white/10"}`} />)}
          </div>

          {step === 1 && (
            <div data-testid="quote-step-1">
              <h2 className="font-serif text-2xl text-rossi-cream mb-6">About your project</h2>
              <label className="text-sm text-rossi-cream/80 mb-2 block">Project type</label>
              <div className="flex flex-wrap gap-2 mb-6">
                {TYPES.map((t) => <button key={t} onClick={() => set("type", t)} className={`px-3 py-2 rounded-full text-sm ${data.type === t ? "bg-rossi-gold text-rossi-bg" : "border border-rossi-gold/30 text-rossi-cream/80"}`}>{t}</button>)}
              </div>
              <input placeholder="Approximate room size (e.g. 3m x 2.5m)" value={data.size} onChange={(e) => set("size", e.target.value)} className="w-full bg-rossi-bg border border-white/10 rounded-lg px-3 py-2.5 text-sm text-rossi-cream mb-3 focus:outline-none focus:border-rossi-gold/50" data-testid="quote-size" />
              <input placeholder="Current condition (e.g. dated, needs strip-out)" value={data.condition} onChange={(e) => set("condition", e.target.value)} className="w-full bg-rossi-bg border border-white/10 rounded-lg px-3 py-2.5 text-sm text-rossi-cream focus:outline-none focus:border-rossi-gold/50" data-testid="quote-condition" />
              <div className="flex justify-end mt-8"><button onClick={() => setStep(2)} className="btn-gold" data-testid="quote-next-1">Continue <ArrowRight size={16} /></button></div>
            </div>
          )}

          {step === 2 && (
            <div data-testid="quote-step-2">
              <h2 className="font-serif text-2xl text-rossi-cream mb-6">Materials & timing</h2>
              <input placeholder="Materials needed (or 'I have my own tiles')" value={data.materials} onChange={(e) => set("materials", e.target.value)} className="w-full bg-rossi-bg border border-white/10 rounded-lg px-3 py-2.5 text-sm text-rossi-cream mb-3 focus:outline-none focus:border-rossi-gold/50" data-testid="quote-materials" />
              <input placeholder="Preferred timeframe (e.g. within 2 months)" value={data.timeframe} onChange={(e) => set("timeframe", e.target.value)} className="w-full bg-rossi-bg border border-white/10 rounded-lg px-3 py-2.5 text-sm text-rossi-cream mb-5 focus:outline-none focus:border-rossi-gold/50" data-testid="quote-timeframe" />
              <label className="text-sm text-rossi-cream/80 mb-2 block">Budget</label>
              <div className="flex flex-wrap gap-2">
                {BUDGETS.map((b) => <button key={b} onClick={() => set("budget", b)} className={`px-4 py-2 rounded-full text-sm ${data.budget === b ? "bg-rossi-gold text-rossi-bg" : "border border-rossi-gold/30 text-rossi-cream/80"}`}>{b}</button>)}
              </div>
              <div className="flex justify-between mt-8"><button onClick={() => setStep(1)} className="btn-ghost-gold"><ArrowLeft size={16} /> Back</button><button onClick={() => setStep(3)} className="btn-gold" data-testid="quote-next-2">Continue <ArrowRight size={16} /></button></div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="quote-step-3">
              <h2 className="font-serif text-2xl text-rossi-cream mb-6">Your details</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {[["name", "Full name"], ["email", "Email"], ["phone", "Phone"], ["postcode", "Postcode"]].map(([k, ph]) => (
                  <input key={k} placeholder={ph} value={data[k]} onChange={(e) => set(k, e.target.value)} className="bg-rossi-bg border border-white/10 rounded-lg px-3 py-2.5 text-sm text-rossi-cream focus:outline-none focus:border-rossi-gold/50" data-testid={`quote-${k}`} />
                ))}
              </div>
              <textarea placeholder="Anything else we should know? (optional)" rows={3} value={data.message} onChange={(e) => set("message", e.target.value)} className="w-full bg-rossi-bg border border-white/10 rounded-lg px-3 py-2.5 text-sm text-rossi-cream mt-3 focus:outline-none focus:border-rossi-gold/50" data-testid="quote-message" />
              <div className="flex justify-between mt-8"><button onClick={() => setStep(2)} className="btn-ghost-gold"><ArrowLeft size={16} /> Back</button><button onClick={submit} className="btn-gold" data-testid="quote-submit">Send quote request</button></div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
